import { format, parseISO, startOfDay, subDays } from "date-fns";
import { formatDateRangePt } from "../../../dashboard-framework/describeWidgetLogic";
import { DateFilterKey, DateRange } from "../../../dashboard-framework/types";
import { FOLLOWUP_SERVICE, REPORTS_FIRST_SERVICE, REPORTS_SERVICE } from "../../../utils/constants";
import { OVERDUE_DAYS } from "./qeegPendingReportsRows";

// Painel "Relatórios por enviar (avaliações qEEG)" — pedido do stakeholder (Jorge, 13–22/06/2026),
// plano em docs/plans/qeeg-pending-reports-dashboard-plan.md. Dashboard independente: só partilha
// a framework de dashboards (infra).
//
// Modelo (validado na coleção `evaluations`):
//  - A "consulta de avaliação" é a SESSÃO DE RELATÓRIO com o psicólogo (report_session — serviços
//    37 = 1ª avaliação, 40/50 = seguimento). O relógio do envio só começa DEPOIS de essa consulta
//    estar CONCLUÍDA (report_session.status === "done"): só aí o psicólogo juntou sintomas + anamnese
//    + EEG e tem tudo para fechar o relatório. Um relatório nunca está "em atraso" antes disso.
//  - "enviado" = evaluation_sent_by preenchido; data de envio = evaluation_date_sent.
//  - técnico do caso = psicólogo da consulta (report_session.technician.user_id).
//  - tempo conta-se em dias de calendário desde o dia da consulta (report_session.start_date).
//  - corte de qualidade de dados: só consultas a partir de 2026-01-01 (registos anteriores são lixo).

export const QEEG_PENDING_REPORTS_KEY = "qeeg-pending-reports";

// Tipo pela sessão de relatório: 37 = 1ª avaliação; 40/50 = seguimento. Multiselect; por omissão só 1ª.
export type ServiceTypeKey = "first" | "followup";

export const SERVICE_TYPE_OPTIONS: Array<{ key: ServiceTypeKey; label: string; serviceIds: Array<number> }> = [
    { key: "first", label: "1ª avaliação", serviceIds: [REPORTS_FIRST_SERVICE] },
    { key: "followup", label: "Seguimento", serviceIds: [REPORTS_SERVICE, FOLLOWUP_SERVICE] },
];

// Por omissão ambos (a vista de gestão mostra sempre as duas tabelas; o filtro só recorta o detalhe).
export const DEFAULT_SERVICE_TYPE_KEYS: Array<ServiceTypeKey> = ["first", "followup"];

// Todos os serviços de relatório — o fetch traz sempre ambos os tipos (cartões e tabelas por técnico
// mostram o quadro completo); o multiselect só filtra a lista de detalhe, client-side.
export const ALL_REPORT_SERVICE_IDS = SERVICE_TYPE_OPTIONS.flatMap((option) => option.serviceIds);

export const serviceTypesLabel = (serviceTypeKeys: Array<ServiceTypeKey>) => {
    const labels = SERVICE_TYPE_OPTIONS.filter((option) => serviceTypeKeys.includes(option.key)).map((option) => option.label);
    const label = labels.join(" + ") || "—";
    return label;
};

// Serviço de relatório → tipo (1ª avaliação / seguimento), para separar tabelas e a coluna "Serviço".
export const reportServiceTypeKey = (serviceId: number | null): ServiceTypeKey | null => {
    const option = SERVICE_TYPE_OPTIONS.find((candidate) => serviceId !== null && candidate.serviceIds.includes(serviceId));
    return option ? option.key : null;
};

export const serviceTypeShortLabel = (serviceTypeKey: ServiceTypeKey | null) => {
    const option = SERVICE_TYPE_OPTIONS.find((candidate) => candidate.key === serviceTypeKey);
    const label = option ? option.label : "—";
    return label;
};

// Corte de qualidade de dados — aplica-se à data da consulta (report_session.start_date).
export const CONSULTATION_CUTOFF_DATE = "2026-01-01";
const CONSULTATION_CUTOFF_LABEL = "01/01/2026";

// Semáforo dos dias à espera: ≤7 verde, 8–10 amarelo, 11+ vermelho (OVERDUE_DAYS vive em Rows).
export const DAYS_BADGE_GREEN_MAX = 7;
export const DAYS_BADGE_YELLOW_MAX = 10;
export const DAYS_BADGE_GREEN = "#2e9e44";
export const DAYS_BADGE_YELLOW = "#EF9F27";
export const DAYS_BADGE_RED = "#e05572";

// Subset de presets do filtro partilhado (DashboardDateFilter acrescenta sempre "Personalizado").
// Migração 2026-07: os períodos deixaram de ser janelas móveis de N dias a terminar hoje —
// "ultima_semana"/"ultimo_mes" são períodos completos anteriores e "ultimos_3/12_meses" são meses
// de calendário incluindo o corrente. O default "ultimos_30_dias" preserva a janela móvel antiga.
// O período só afeta "Feitos" e "Tempo médio" (cartões, colunas/linhas das tabelas por técnico e
// drills de enviados) — os pendentes (Em atraso / Pendentes / lista) ignoram-no por definição.
export const QEEG_PERIOD_PRESETS: Array<DateFilterKey> = ["ultimos_30_dias", "ultima_semana", "ultimo_mes", "ultimos_3_meses", "ultimos_12_meses"];

const formatDay = (date: Date) => {
    const formatted = format(date, "yyyy-MM-dd");
    return formatted;
};

// Data a partir da qual uma consulta concluída está "em atraso" (mais de OVERDUE_DAYS dias).
export const overdueBeforeDay = (now: Date) => {
    const cutoff = formatDay(subDays(startOfDay(now), OVERDUE_DAYS));
    return cutoff;
};

// Campos sempre enumerados (nunca wildcards). report_session.technician.user_id faz a ponte para
// directus_users (chave de rollup); o nome é resolvido por lookup à parte.
export const EVALUATION_FIELDS = "id,evaluation_date_sent,report_session.service,report_session.start_date,report_session.technician.user_id,treatment.id";
export const PEOPLE_FIELDS = "id,first_name,last_name,email";
export const PEOPLE_LOOKUP_CHUNK_SIZE = 80;

// Vista pessoal de um psicólogo: restringe ao próprio responsável pela consulta de relatório.
const ownTechnicianClause = (ownTechnicianUserId: string) => {
    const clause = { report_session: { technician: { user_id: { _eq: ownTechnicianUserId } } } };
    return clause;
};

// Universo: avaliação cuja consulta de relatório (dos tipos selecionados) está CONCLUÍDA, a partir do corte.
const reportConsultationDoneClauses = (serviceIds: Array<number>) => {
    const clauses = [
        { report_session: { service: { _in: serviceIds } } },
        { report_session: { status: { _eq: "done" } } },
        { report_session: { start_date: { _gte: CONSULTATION_CUTOFF_DATE } } },
    ];
    return clauses;
};

// Pendentes: consulta concluída E relatório por enviar (evaluation_sent_by vazio). Sem filtro de período.
export const pendingEvaluationsFilter = (ownTechnicianUserId: string | null, serviceIds: Array<number>) => {
    const filter = {
        _and: [
            ...reportConsultationDoneClauses(serviceIds),
            { evaluation_sent_by: { _null: true } },
            ...(ownTechnicianUserId ? [ownTechnicianClause(ownTechnicianUserId)] : []),
        ],
    };
    return filter;
};

// Subconjunto em atraso (consulta concluída há mais de OVERDUE_DAYS dias) — para o drill do cartão.
export const overduePendingFilter = (ownTechnicianUserId: string | null, serviceIds: Array<number>, now: Date) => {
    const base = pendingEvaluationsFilter(ownTechnicianUserId, serviceIds);
    const filter = { _and: [...base._and, { report_session: { start_date: { _lt: overdueBeforeDay(now) } } }] };
    return filter;
};

// Enviados no período: relatório enviado (evaluation_sent_by preenchido) com data de envio no período.
export const sentEvaluationsFilter = (range: DateRange, ownTechnicianUserId: string | null, serviceIds: Array<number>) => {
    const filter = {
        _and: [
            ...reportConsultationDoneClauses(serviceIds),
            { evaluation_sent_by: { _nnull: true } },
            { evaluation_date_sent: { _gte: range.gte, _lt: range.lt } },
            ...(ownTechnicianUserId ? [ownTechnicianClause(ownTechnicianUserId)] : []),
        ],
    };
    return filter;
};

// Drill dos cartões (modal "ver dados subjacentes"): as MESMAS avaliações que originam cada número.
const formatTimestampPt = (isoTimestamp: string | null | undefined) => {
    if (!isoTimestamp) return "—";
    const formatted = format(parseISO(isoTimestamp), "dd/MM/yyyy");
    return formatted;
};

export const DRILL_FIELDS = "id,evaluation_date_sent,report_session.start_date,treatment.id";

export const pendingDrillColumns = [
    { key: "treatment", label: "Tratamento", valueFor: (item: any) => item.treatment?.id ?? "—" },
    { key: "consulta", label: "Consulta de avaliação", sortField: "report_session.start_date", valueFor: (item: any) => formatTimestampPt(item.report_session?.start_date) },
];

export const sentDrillColumns = [
    { key: "treatment", label: "Tratamento", valueFor: (item: any) => item.treatment?.id ?? "—" },
    { key: "consulta", label: "Consulta de avaliação", sortField: "report_session.start_date", valueFor: (item: any) => formatTimestampPt(item.report_session?.start_date) },
    { key: "enviado", label: "Relatório enviado", sortField: "evaluation_date_sent", valueFor: (item: any) => formatTimestampPt(item.evaluation_date_sent) },
];

export const drillTreatmentRoute = (item: any) => {
    const route = item.treatment?.id ? `/treatments/${item.treatment.id}/detail` : null;
    return route;
};

// Flag de qualidade de dados: consultas agendadas (booked/confirmed) com a data JÁ PASSADA e o
// relatório por enviar — sinal de que a sessão não foi fechada após uma falta/remarcação (a data
// fica congelada no dia antigo). Estes casos nunca entram nos pendentes (não estão "done"), por
// isso passam despercebidos; o flag torna-os visíveis para a equipa regularizar os dados.
export const staleConsultationsFilter = (ownTechnicianUserId: string | null, serviceIds: Array<number>, now: Date) => {
    const filter = {
        _and: [
            { report_session: { service: { _in: serviceIds } } },
            { report_session: { status: { _in: ["booked", "confirmed"] } } },
            { report_session: { start_date: { _gte: CONSULTATION_CUTOFF_DATE, _lt: formatDay(startOfDay(now)) } } },
            { evaluation_sent_by: { _null: true } },
            ...(ownTechnicianUserId ? [ownTechnicianClause(ownTechnicianUserId)] : []),
        ],
    };
    return filter;
};

export const STALE_DRILL_FIELDS = "id,report_session.status,report_session.start_date,treatment.id";

export const staleDrillColumns = [
    { key: "treatment", label: "Tratamento", valueFor: (item: any) => item.treatment?.id ?? "—" },
    { key: "consulta", label: "Consulta agendada", sortField: "report_session.start_date", valueFor: (item: any) => formatTimestampPt(item.report_session?.start_date) },
    { key: "estado", label: "Estado da sessão", valueFor: (item: any) => item.report_session?.status ?? "—" },
];

export const describeStaleConsultationsPt = () => {
    const description =
        `Consultas de avaliação agendadas (estado "booked"/"confirmed") cuja data já passou e o relatório ainda não foi enviado — ` +
        `sinal de que a sessão não foi atualizada após uma falta ou remarcação. Atualiza o estado (concluída / remarcada / cancelada) para regularizar.`;
    return description;
};

// Tempo médio "X,Y dias" (uma casa decimal, vírgula decimal pt-PT) ou "—" quando não há envios.
export const formatAverageDays = (averageDays: number | null) => {
    if (averageDays === null) return "—";
    const rounded = Math.round(averageDays * 10) / 10;
    const wholePart = Math.trunc(rounded);
    const decimalDigit = Math.abs(Math.round((rounded - wholePart) * 10));
    const formatted = `${wholePart},${decimalDigit} dias`;
    return formatted;
};

const ownViewNote = (isOwnView: boolean) => (isOwnView ? " Vista pessoal: apenas os teus casos." : "");

// Explicações ⓘ obrigatórias — geradas a partir do MESMO período/critérios das queries.
export const describeSummaryPt = (range: DateRange, isOwnView: boolean, typesLabel: string) => {
    const description =
        `Avaliações qEEG (${typesLabel}) cuja consulta de avaliação com o psicólogo já foi concluída (a partir de ${CONSULTATION_CUTOFF_LABEL}) — ` +
        `o relógio do envio só começa depois dessa consulta. ` +
        `Em atraso e Pendentes = todos os casos por enviar até hoje (não dependem do período); ` +
        `Relatórios feitos e Tempo médio = relatórios enviados ${formatDateRangePt(range)}. ` +
        `Em atraso = por enviar há mais de ${OVERDUE_DAYS} dias; ` +
        `Tempo médio = média de dias de calendário entre a consulta e o envio.${ownViewNote(isOwnView)}`;
    return description;
};

export const describeTechnicianTablePt = (range: DateRange, typesLabel: string) => {
    const description =
        `Uma linha por psicólogo responsável pela consulta de avaliação (${typesLabel}, a partir de ${CONSULTATION_CUTOFF_LABEL}). ` +
        `Em atraso e Pendentes são o stock atual por enviar (sem período); ` +
        `Feitos e Tempo médio referem-se aos relatórios enviados ${formatDateRangePt(range)}.`;
    return description;
};

export const describePendingListPt = (isOwnView: boolean, typesLabel: string) => {
    const description =
        `Todas as avaliações (${typesLabel}) com a consulta de avaliação concluída (a partir de ${CONSULTATION_CUTOFF_LABEL}) e o relatório por enviar, ` +
        `da mais antiga para a mais recente (não depende do período). ` +
        `Dias à espera contam, em calendário, desde o dia da consulta até hoje ` +
        `(≤${DAYS_BADGE_GREEN_MAX} verde, ${DAYS_BADGE_GREEN_MAX + 1}–${DAYS_BADGE_YELLOW_MAX} amarelo, ${DAYS_BADGE_YELLOW_MAX + 1}+ vermelho). ` +
        `Um cliente que desistiu continua aqui até o relatório ser enviado.${ownViewNote(isOwnView)}`;
    return description;
};
